import { useState } from 'react'
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Star, LogIn, Eye } from 'lucide-react'

interface KeywordRow {
  id: number
  keyword: string
  position: number | null
  delta: number | null
  volume: number | null
  url: string | null
}

interface KeywordBlocksProps {
  keywords: KeywordRow[]
  onFilterChange: (filter: string) => void
}

interface BlockConfig {
  id: string
  title: string
  icon: React.ReactNode
  color: string
  bgColor: string
  borderColor: string
  filter: (kw: KeywordRow) => boolean
  sort: (a: KeywordRow, b: KeywordRow) => number
  badge: (kw: KeywordRow) => React.ReactNode
}

function PositionBadge({ position }: { position: number | null }) {
  if (position === null) return <span className="text-xs text-[#8B8FA7]">—</span>
  const cls = position === 1 ? 'bg-emerald-500/15 text-emerald-600 font-bold'
    : position <= 3 ? 'bg-green-500/15 text-green-600'
    : position <= 10 ? 'bg-blue-500/15 text-blue-600'
    : position <= 30 ? 'bg-yellow-500/15 text-yellow-600'
    : 'bg-gray-100 text-gray-500'
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold tabular-nums ${cls}`}>#{position}</span>
}

function DeltaInline({ delta }: { delta: number | null }) {
  if (delta === null) return null
  if (delta > 0) return <span className="text-emerald-500 text-[11px] font-medium">+{delta}</span>
  if (delta < 0) return <span className="text-red-400 text-[11px] font-medium">{delta}</span>
  return null
}

export default function KeywordBlocks({ keywords, onFilterChange }: KeywordBlocksProps) {
  const blocks: BlockConfig[] = [
    {
      id: 'entered-top10',
      title: 'Entrées dans le Top 10',
      icon: <LogIn className="w-4 h-4" strokeWidth={1.25} />,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      filter: kw => kw.position !== null && kw.position <= 10 && kw.delta !== null && kw.delta > 0,
      sort: (a, b) => (a.position ?? 999) - (b.position ?? 999),
      badge: kw => <PositionBadge position={kw.position} />,
    },
    {
      id: 'entered-top100',
      title: 'Entrées dans le Top 100',
      icon: <Eye className="w-4 h-4" strokeWidth={1.25} />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      filter: kw => kw.position !== null && kw.delta === null,
      sort: (a, b) => (a.position ?? 999) - (b.position ?? 999),
      badge: kw => <PositionBadge position={kw.position} />,
    },
    {
      id: 'best',
      title: 'Meilleurs mots-clés',
      icon: <Star className="w-4 h-4" strokeWidth={1.25} />,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      filter: kw => kw.position !== null,
      sort: (a, b) => (a.position ?? 999) - (b.position ?? 999),
      badge: kw => <PositionBadge position={kw.position} />,
    },
    {
      id: 'improved',
      title: 'Mots-clés améliorés',
      icon: <TrendingUp className="w-4 h-4" strokeWidth={1.25} />,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      filter: kw => kw.delta !== null && kw.delta > 0,
      sort: (a, b) => (b.delta ?? 0) - (a.delta ?? 0),
      badge: kw => (
        <span className="flex items-center gap-1">
          <PositionBadge position={kw.position} />
          <ArrowUpRight className="w-3 h-3 text-emerald-500" strokeWidth={2} />
          <span className="text-emerald-500 text-[11px] font-semibold">+{kw.delta}</span>
        </span>
      ),
    },
    {
      id: 'declined',
      title: 'Mots-clés en recul',
      icon: <TrendingDown className="w-4 h-4" strokeWidth={1.25} />,
      color: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      filter: kw => kw.delta !== null && kw.delta < 0,
      sort: (a, b) => (a.delta ?? 0) - (b.delta ?? 0),
      badge: kw => (
        <span className="flex items-center gap-1">
          <PositionBadge position={kw.position} />
          <ArrowDownRight className="w-3 h-3 text-red-400" strokeWidth={2} />
          <span className="text-red-400 text-[11px] font-semibold">{kw.delta}</span>
        </span>
      ),
    },
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      {blocks.map(block => {
        const filtered = keywords.filter(block.filter).sort(block.sort)
        const display = filtered.slice(0, 5)
        const total = filtered.length

        return (
          <div key={block.id} className={`bg-white border ${block.borderColor} rounded-xl overflow-hidden`}>
            {/* Header */}
            <div className={`${block.bgColor} px-4 py-2.5 flex items-center gap-2`}>
              <span className={block.color}>{block.icon}</span>
              <span className={`text-xs font-semibold ${block.color}`}>{block.title}</span>
              <span className={`ml-auto text-[10px] font-bold ${block.color} bg-white/60 px-1.5 py-0.5 rounded-full`}>{total}</span>
            </div>

            {/* List */}
            <div className="divide-y divide-[#F4F5FA]">
              {display.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <p className="text-xs text-[#8B8FA7]">Aucun mot-clé</p>
                </div>
              ) : (
                display.map(kw => (
                  <div key={kw.id} className="px-4 py-2 flex items-center justify-between gap-2 hover:bg-[#F4F5FA]/50 transition-colors">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-[#1B2559] truncate">{kw.keyword}</p>
                      {kw.volume && (
                        <p className="text-[10px] text-[#8B8FA7]">{kw.volume.toLocaleString('fr-FR')} vol.</p>
                      )}
                    </div>
                    <div className="shrink-0">{block.badge(kw)}</div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {total > 5 && (
              <div className="border-t border-[#F4F5FA]">
                <button
                  onClick={() => onFilterChange(block.id)}
                  className={`w-full px-4 py-2 text-[11px] font-medium ${block.color} hover:${block.bgColor} transition-colors text-center`}
                >
                  Voir tout ({total})
                </button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
